import { Component, EventEmitter, Injectable, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { SurveyService } from '../../../service/survey.service';
import { MessageService } from 'src/app/service/message.service';
import { SurveyNewProtectionFisRequest } from 'src/app/model/survey-request';
import { SurveyProtectionResponse } from 'src/app/model/survey-response';
import { decodeFlag, encodeFlag, formatNgbDate, parseNgbDate } from 'src/app/utils/common.functions';
import { NgbDateParserFormatter, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { FadeInOut } from 'src/app/utils/animation';
import { NdgDataEmit } from 'src/app/model/survey-emits';

@Injectable()
export class CustomDateParserFormatter extends NgbDateParserFormatter {
  readonly DELIMITER = '/';

  parse(value: string): NgbDateStruct | null {
    if (value) {
      let date = value.split(this.DELIMITER);

      return {
        day : parseInt(date[0], 10),
        month : parseInt(date[1], 10),
        year : parseInt(date[2], 10)
      };
    }

    return null;
  }

  format(date: NgbDateStruct | null): string {
    return date ? ("00" + date.day).slice(-2) + '/' + ("00" + date.month).slice(-2) + '/' + date.year : '';
  }
}

@Component({
  selector: 'protection',
  templateUrl: './protection.component.html',
  animations: [FadeInOut()],
  providers: [
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }
  ],
})
export class ProtectionComponent implements OnChanges {
  @Input() surveyId!: string;
  @Input() ndgData!: NdgDataEmit;
  @Input() protection?: SurveyProtectionResponse;
  @Output() complete = new EventEmitter<void>();

  formGroup!: FormGroup;
  isNdgRetired!: boolean;
  loading: boolean = false;
  completed: boolean = false;

  constructor(
    private surveyService: SurveyService,
    private messageService: MessageService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Check if the surveyId (new survey) or protection (show survey) inputs have changed
    if((changes['surveyId'] && changes['surveyId'].firstChange) || changes['protection']) {
      this.init();
    }
  }

  init(): void {
    this.formGroup = new FormGroup({
      posthumousPolicy: new FormControl(encodeFlag(this.protection?.posthumousPolicy), Validators.required),
      dangerousProfession: new FormControl(encodeFlag(this.protection?.dangerousProfession), Validators.required),
      sport: new FormControl(this.protection?.sport, Validators.required),
      pets: new FormControl(encodeFlag(this.protection?.pets), Validators.required),
      petsProtection: new FormControl(this.protection?.petsProtection, Validators.required),
      otherProtection: new FormControl(encodeFlag(this.protection?.otherProtection), Validators.required),
      familyProtection: new FormControl(encodeFlag(this.protection?.familyProtection), Validators.required),
      injuries: new FormControl(this.protection?.injuries, Validators.required),
      selfSufficiency: new FormControl(this.protection?.selfSufficiency, Validators.required),
      ssn: new FormControl(encodeFlag(this.protection?.ssn), Validators.required),
      ssnFees: new FormControl(this.protection?.ssnFees || 0, Validators.required),
      healthCoverages: new FormControl(this.protection?.healthCoverages || []),
      funding: new FormControl(encodeFlag(this.protection?.funding), Validators.required),
      fundingAmount: new FormControl(this.protection?.fundingAmount || 0, Validators.required),
      fundingDueDate: new FormControl(parseNgbDate(this.protection?.fundingDueDate), Validators.required),
      movableProperty: new FormControl(encodeFlag(this.protection?.movableProperty), Validators.required),
      property: new FormControl(this.protection?.property, Validators.required),
      propertyProtection: new FormControl(this.protection?.propertyProtection, Validators.required),
      priorityGuarantees: new FormControl(this.protection?.priorityGuarantees || []),
      propertyCoverages: new FormControl(this.protection?.propertyCoverages || []),
      bnb: new FormControl(encodeFlag(this.protection?.bnb), Validators.required),
      internet: new FormControl(encodeFlag(this.protection?.internet), Validators.required),
      vehicle: new FormControl(this.protection?.vehicle || []),
      activeCoverages: new FormControl(this.protection?.activeCoverages || []),
      coverageAmount: new FormControl(this.protection?.coverageAmount || 0, Validators.required),
      coverageAlternative: new FormControl(encodeFlag(this.protection?.coverageAlternative), Validators.required),
      coverageDuration: new FormControl(this.protection?.coverageDuration, Validators.required),
      temporarySolutions: new FormControl(encodeFlag(this.protection?.temporarySolutions), Validators.required),
      coverageTrivia: new FormControl(encodeFlag(this.protection?.coverageTrivia), Validators.required)
    });

    this.isNdgRetired = this.ndgData.occupation === 'RIT';

    if(!this.isNdgRetired) {
      this.posthumousPolicy?.disable();
    }

    this.petsProtection?.disable(); //enabled only when pets === 'Y'
    this.fundingAmount?.disable(); //enabled only when funding === 'Y'
    this.fundingDueDate?.disable(); //enabled only when funding === 'Y'
    this.familyProtection?.disable(); //enabled only when otherProtection === 'Y'
    this.injuries?.disable(); //enabled only when otherProtection === 'Y'
    this.ssnFees?.disable(); //enabled only when ssn === 'Y'

    this.formGroup.updateValueAndValidity();

    this.pets?.valueChanges.subscribe({
      next: (value: string) => {
        if(value === 'Y') {
          this.petsProtection?.enable();
        } else {
          this.petsProtection?.disable();
        }
      }
    });

    this.funding?.valueChanges.subscribe({
      next: (value: string) => {
        if(value === 'Y') {
          this.fundingAmount?.enable();
          this.fundingDueDate?.enable();
        } else {
          this.fundingAmount?.disable();
          this.fundingDueDate?.disable();
        }
      }
    });

    this.otherProtection?.valueChanges.subscribe({
      next: (value: string) => {
        if(value === 'Y') {
          this.familyProtection?.enable();
          this.injuries?.enable();
        } else {
          this.familyProtection?.disable();
          this.injuries?.disable();
        }
      }
    });

    this.ssn?.valueChanges.subscribe({
      next: (value: string) => {
        if(value === 'Y') {
          this.ssnFees?.enable();
        } else {
          this.ssnFees?.disable();
        }
      }
    });

    if(!this.isInsert()) {
      this.completed = true;
      this.formGroup.disable();
    }
  }

  isInsert(): boolean {
    return this.protection === undefined;
  }

  get posthumousPolicy() { return this.formGroup.get('posthumousPolicy') }
  get dangerousProfession() { return this.formGroup.get('dangerousProfession') }
  get sport() { return this.formGroup.get('sport') }
  get pets() { return this.formGroup.get('pets') }
  get petsProtection() { return this.formGroup.get('petsProtection') }
  get otherProtection() { return this.formGroup.get('otherProtection') }
  get familyProtection() { return this.formGroup.get('familyProtection') }
  get injuries() { return this.formGroup.get('injuries') }
  get selfSufficiency() { return this.formGroup.get('selfSufficiency') }
  get ssn() { return this.formGroup.get('ssn') }
  get ssnFees() { return this.formGroup.get('ssnFees') }
  get healthCoverages() { return this.formGroup.get('healthCoverages') }
  get funding() { return this.formGroup.get('funding') }
  get fundingAmount() { return this.formGroup.get('fundingAmount') }
  get fundingDueDate() { return this.formGroup.get('fundingDueDate') }
  get movableProperty() { return this.formGroup.get('movableProperty') }
  get property() { return this.formGroup.get('property') }
  get propertyProtection() { return this.formGroup.get('propertyProtection') }
  get priorityGuarantees() { return this.formGroup.get('priorityGuarantees') }
  get propertyCoverages() { return this.formGroup.get('propertyCoverages') }
  get bnb() { return this.formGroup.get('bnb') }
  get internet() { return this.formGroup.get('internet') }
  get vehicle() { return this.formGroup.get('vehicle') }
  get activeCoverages() { return this.formGroup.get('activeCoverages') }
  get coverageAmount() { return this.formGroup.get('coverageAmount') }
  get coverageAlternative() { return this.formGroup.get('coverageAlternative') }
  get coverageDuration() { return this.formGroup.get('coverageDuration') }
  get temporarySolutions() { return this.formGroup.get('temporarySolutions') }
  get coverageTrivia() { return this.formGroup.get('coverageTrivia') }

  onCheckChange(field: AbstractControl | null, event: MatCheckboxChange) {
    if(event.checked) {
      field?.value.push(event.source.value);
    } else {
      const index = field?.value.indexOf(event.source.value, 0);
      if (index > -1) {
        field?.value.splice(index, 1);
      }
    }
  }

  hasError(field: AbstractControl | null) {
    return !!field && field.touched && !field.disabled && !field.valid;
  }

  onSubmit() {
    if(!this.formGroup.valid || this.completed) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.formGroup.disable();
    this.completed = true;
    this.loading = true;

    const payload: SurveyNewProtectionFisRequest = {
      posthumousPolicy: decodeFlag(this.posthumousPolicy?.value),
      dangerousProfession: decodeFlag(this.dangerousProfession?.value),
      sport: this.sport?.value,
      pets: decodeFlag(this.pets?.value),
      petsProtection: this.petsProtection?.value,
      otherProtection: decodeFlag(this.otherProtection?.value),
      familyProtection: decodeFlag(this.familyProtection?.value),
      injuries: this.injuries?.value,
      selfSufficiency: this.selfSufficiency?.value,
      ssn: decodeFlag(this.ssn?.value),
      ssnFees: this.ssnFees?.value,
      healthCoverages: this.healthCoverages?.value,
      funding: decodeFlag(this.funding?.value),
      fundingAmount: this.fundingAmount?.value,
      fundingDueDate: formatNgbDate(this.fundingDueDate?.value),
      movableProperty: decodeFlag(this.movableProperty?.value),
      property: this.property?.value,
      propertyProtection: this.propertyProtection?.value,
      priorityGuarantees: this.priorityGuarantees?.value,
      propertyCoverages: this.propertyCoverages?.value,
      bnb: decodeFlag(this.bnb?.value),
      internet: decodeFlag(this.internet?.value),
      vehicle: this.vehicle?.value,
      activeCoverages: this.activeCoverages?.value,
      coverageAmount: this.coverageAmount?.value,
      coverageAlternative: decodeFlag(this.coverageAlternative?.value),
      coverageDuration: this.coverageDuration?.value,
      temporarySolutions: decodeFlag(this.temporarySolutions?.value),
      coverageTrivia: decodeFlag(this.coverageTrivia?.value)
    }

    this.surveyService.insertProtection(this.surveyId, payload).subscribe({
      next: () => {
        this.loading = false;
        this.complete.emit();
      },
      error: () => {
        this.messageService.error('survey.new.message.unknown-error');

        this.loading = false;
        this.completed = false;
        this.formGroup.enable();
      }
    });
  }
} 
